import {
  CONDITION_LABELS,
  type DeviceMessageParts,
  type NotificationCondition
} from './batteryOfflineNotifications'

/**
 * Visible styling for the device toasts. The pure module owns *what* the toast
 * says (labels, descriptor parts); this file owns *how* it looks, and exports a
 * component only so the `react-refresh/only-export-components` rule stays happy.
 *
 * There are no icons — the condition is conveyed by colouring the key word(s) of
 * the message with the app status tokens: green (`text-good`) for a recovery,
 * red (`text-bad`) for offline, amber (`text-average`) for critical battery. The
 * design system has no literal "orange"; amber is its warning colour, so
 * battery-critical matches the rest of the app.
 */
type ConditionPresentation = {
  /** The substring of the condition label to emphasise (bold + coloured). */
  emphasis: string
  emphasisClassName: string
}

const CONDITION_PRESENTATION: Record<
  NotificationCondition,
  ConditionPresentation
> = {
  online: { emphasis: 'online', emphasisClassName: 'font-bold text-good' },
  offline: { emphasis: 'offline', emphasisClassName: 'font-bold text-bad' },
  battery: {
    // A critical battery emphasises the whole phrase, not a single word.
    emphasis: CONDITION_LABELS.battery,
    emphasisClassName: 'font-bold text-average'
  }
}

/**
 * The visible toast content: the device descriptor with its id in bold, then the
 * condition phrase with the key word(s) bold + coloured. Renders the same parts
 * as the plain-text `buildMessage` (the accessible label), adding only styling.
 */
export const DeviceToastMessage = ({
  parts,
  condition
}: {
  parts: DeviceMessageParts
  condition: NotificationCondition
}) => {
  const label = CONDITION_LABELS[condition]
  const { emphasis, emphasisClassName } = CONDITION_PRESENTATION[condition]

  // Split the label so only the emphasised word(s) are styled.
  const start = label.indexOf(emphasis)
  const before = start >= 0 ? label.slice(0, start) : label
  const after = start >= 0 ? label.slice(start + emphasis.length) : ''

  const lead = [
    parts.typeLabel,
    parts.name || undefined,
    parts.number != null ? `#${parts.number}` : undefined
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span>
      {lead} <strong>({parts.id})</strong>
      {' — '}
      {before}
      <strong className={emphasisClassName}>{emphasis}</strong>
      {after}
    </span>
  )
}
